import { Trans, t } from '@lingui/macro'
import { Card, CardContent, Typography, Grid, Box, Skeleton } from '@mui/material'
import DescriptionIcon from '@mui/icons-material/Description'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import { AIUsageStatistics } from '@/core/api/statistics'
import { ReactNode } from 'react'

type AIUsageOverviewComponentProps = {
  data: AIUsageStatistics | undefined
  isLoading: boolean
}

type StatItemProps = {
  label: string
  total: number
  description: ReactNode
  icon: ReactNode
  gradient: string
  isLoading: boolean
}

function StatItem({ label, total, description, icon, gradient, isLoading }: StatItemProps) {
  return (
    <Grid item xs={12} md={6}>
      <Card
        sx={{
          height: '100%',
          background: isLoading ? 'transparent' : gradient,
          color: isLoading ? 'text.secondary' : 'white',
          border: isLoading ? '1px solid' : 'none',
          borderColor: 'divider',
          minHeight: 180,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {isLoading ? (
              <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
            ) : (
              <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', fontSize: 40 }}>{icon}</Box>
            )}
            <Typography variant="h6">{isLoading ? <Skeleton width={100} /> : label}</Typography>
          </Box>
          <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
            {isLoading ? <Skeleton width="40%" /> : total}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            {isLoading ? <Skeleton width="70%" /> : description}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  )
}

export function AIUsageOverviewComponent({ data, isLoading }: AIUsageOverviewComponentProps) {
  const stats = [
    {
      label: t`Transcriptions`,
      total: data?.transcriptions?.total ?? 0,
      description: <Trans>AI-generated transcriptions</Trans>,
      icon: <RecordVoiceOverIcon fontSize="inherit" />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      label: t`Reports`,
      total: data?.reports?.total ?? 0,
      description: <Trans>AI-generated medical reports</Trans>,
      icon: <DescriptionIcon fontSize="inherit" />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
  ]

  return (
    <Grid container spacing={3}>
      {stats.map((stat, index) => (
        <StatItem key={index} {...stat} isLoading={isLoading} />
      ))}
    </Grid>
  )
}
